import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import UserMenu from '../user/UserMenu';
import { AppSettingsModal } from '../modals';
import AttachmentLibraryModal from '../ideas/AttachmentLibraryModal';
import { User, Account } from '../../api/structs/user';
import { ThemeMode, RoundedMode } from '../../theme';
import { useAuth } from '../../context/AuthContext';
import { useCredits } from '../../hooks/useCredits';
import { useAccess } from '../../context/AccessContext';
import CreditsChip from './CreditsChip';

interface HeaderProps {
  users: User[];
  activeUser: User | null;
  activeAccount: Account | null;
  onAddUser: () => void;
  onSelectUser: (id: number) => void;
  onRemoveUser: (id: number) => void;
  onSelectAccount: (id: string) => void;
  themeMode: ThemeMode;
  roundedMode: RoundedMode;
  onThemeToggle: () => void;
  onRoundedToggle: () => void;
}

const navLinkClassDesktop = ({ isActive }: { isActive: boolean }) =>
  `px-3 py-1.5 rounded-none text-sm font-bold uppercase tracking-widest border transition-all ${
    isActive 
      ? 'border-[var(--accent-strong)] bg-[var(--accent-strong)] text-[var(--accent-strong-foreground)] shadow-[2px_2px_0px_0px_var(--accent-strong)] translate-x-[-1px] translate-y-[-1px]' 
      : 'border-[var(--border-subtle)] bg-[var(--surface-1)] text-[var(--app-text)] shadow-[2px_2px_0px_0px_var(--border-subtle)] hover:shadow-[3px_3px_0px_0px_var(--border-strong)] hover:-translate-x-px hover:-translate-y-px'
  }`;

const navLinkClassMobile = ({ isActive }: { isActive: boolean }) =>
  `block w-full px-4 py-3 text-left font-bold uppercase tracking-widest border-b border-[var(--border-subtle)] ${
    isActive ? 'bg-[var(--accent-strong)] text-[var(--accent-strong-foreground)]' : 'text-[var(--app-text)] hover:bg-[var(--surface-2)]'
  }`;

const actionLinkClassMobile =
  'block w-full px-4 py-3 text-left font-bold uppercase tracking-widest border-b border-[var(--border-strong)] text-[var(--app-text)] bg-[var(--surface-2)] hover:bg-[var(--surface-3)]';

const Header: React.FC<HeaderProps> = ({
  users,
  activeUser,
  activeAccount,
  onAddUser,
  onSelectUser,
  onRemoveUser,
  onSelectAccount,
  themeMode,
  roundedMode,
  onThemeToggle,
  onRoundedToggle,
}) => {
  const [showEnhancerSettings, setShowEnhancerSettings] = useState(false);
  // The media library is a top-bar affordance, not a per-route one: owning the
  // modal here (like AppSettingsModal) makes it reachable from every page
  // instead of only from the Studio shell that used to host it.
  const [showUploadLibrary, setShowUploadLibrary] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user: authUser, logout } = useAuth();
  const { isAdmin } = useCredits();
  const { canSeeTab, hasFeature } = useAccess();

  const toggleMobileMenu = () => setMobileMenuOpen((prev) => !prev);

  return (
    <>
      <header className="page-header flex-shrink-0 relative z-[130]">
        <div className="page-container py-4">
          {/* Row 1: brand + account controls */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                className="lg:hidden flex items-center justify-center p-2.5 mr-1 -ml-1 text-[var(--app-text)] hover:text-[var(--app-text-muted)] focus:outline-none"
                onClick={toggleMobileMenu}
                aria-label="Toggle navigation menu"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              </button>
              <img 
                src={themeMode === 'light' ? "/LOGO_LIGHTMODE.png" : "/LOGO.png"} 
                alt="ContentGen Logo" 
                className="h-10 md:h-14 lg:h-16 w-auto shrink-0 object-contain" 
              />
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <CreditsChip />

              {hasFeature('upload_media') && (
                <button
                  type="button"
                  onClick={() => setShowUploadLibrary(true)}
                  aria-label="Upload Media"
                  title="Upload Media"
                  className="flex items-center justify-center w-11 h-11 p-2 shrink-0 text-[var(--app-text-muted)] hover:text-[var(--app-text)] transition-colors border border-[var(--border-subtle)] bg-[var(--surface-1)] shadow-[2px_2px_0px_0px_var(--border-subtle)]"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </button>
              )}

              <div className="flex items-center gap-2 shrink-0">
                <div className="shrink-0">
                  <UserMenu
                    users={users}
                    activeUser={activeUser}
                    activeAccount={activeAccount}
                    onAddUser={onAddUser}
                    onSelectUser={onSelectUser}
                    onRemoveUser={onRemoveUser}
                    onSelectAccount={onSelectAccount}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setShowEnhancerSettings(true)}
                  title="App Settings"
                  className="flex items-center justify-center w-11 h-11 p-2 shrink-0 text-[var(--app-text-muted)] hover:text-[var(--app-text)] transition-colors border border-[var(--border-subtle)] bg-[var(--surface-1)] shadow-[2px_2px_0px_0px_var(--border-subtle)]"
                >
                  {/* viewBox padded to 26 + overflow-visible: the cog's outer
                      stroke reaches the 0–24 edges, so a tight 24 viewBox with
                      the default svg overflow:hidden shears all four teeth. */}
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="-1 -1 26 26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="overflow-visible">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </button>

                {authUser && (
                  <div className="hidden md:flex items-center shrink-0 border border-[var(--border-subtle)] bg-[var(--surface-1)] shadow-[2px_2px_0px_0px_var(--border-subtle)]">
                    <div className="px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-[var(--app-text)] border-r border-[var(--border-subtle)] bg-[var(--surface-2)]">
                      {authUser.display_name}
                    </div>
                    <button
                      type="button"
                      onClick={logout}
                      title="Sign out"
                      className="flex items-center justify-center w-8 h-8 shrink-0 text-[var(--app-text-muted)] hover:text-[#ef4444] transition-colors bg-[var(--surface-1)] hover:bg-[var(--surface-2)]"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex mt-4 flex-wrap items-center gap-3 pt-4">
            {canSeeTab('studio') && <NavLink to="/studio" className={navLinkClassDesktop}>Studio</NavLink>}
            {canSeeTab('generate') && <NavLink to="/generate" className={navLinkClassDesktop}>Generate</NavLink>}
            {canSeeTab('series') && <NavLink to="/series" className={navLinkClassDesktop}>Series</NavLink>}
            {canSeeTab('docs') && <NavLink to="/docs" className={navLinkClassDesktop}>Docs</NavLink>}
            {canSeeTab('builder') && <NavLink to="/builder" className={navLinkClassDesktop}>Builder</NavLink>}
            {canSeeTab('chat') && <NavLink to="/chat" className={navLinkClassDesktop}>Chat</NavLink>}
            {canSeeTab('generators') && <NavLink to="/generators" className={navLinkClassDesktop}>Generators</NavLink>}
            {canSeeTab('datasets') && <NavLink to="/datasets" className={navLinkClassDesktop}>Datasets</NavLink>}
            {canSeeTab('training') && <NavLink to="/training" className={navLinkClassDesktop}>Training</NavLink>}
            {canSeeTab('pipeline') && <NavLink to="/pipeline" className={navLinkClassDesktop}>Pipeline</NavLink>}
            {canSeeTab('archive') && <NavLink to="/archive" className={navLinkClassDesktop}>Archive</NavLink>}
            {hasFeature('upload_media') && <NavLink to="/media" className={navLinkClassDesktop}>Media</NavLink>}
            {isAdmin && <NavLink to="/admin" className={navLinkClassDesktop}>Admin</NavLink>}
          </div>

        </div>
      </header>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[200] flex">
          <div className="fixed inset-0 bg-[rgba(0,0,0,0.6)] backdrop-blur-sm" onClick={toggleMobileMenu}></div>
          <div className="relative w-64 max-w-[80vw] h-full bg-[var(--surface-1)] border-r border-[var(--border-strong)] shadow-[8px_0_0_0_var(--border-strong)] flex flex-col animate-slide-right">
            <div className="p-4 border-b border-[var(--border-strong)] flex items-center justify-between">
              <span className="font-bold uppercase tracking-widest">Menu</span>
              <button onClick={toggleMobileMenu} className="p-2 text-[var(--app-text)]">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto py-2">
              {canSeeTab('studio') && <NavLink to="/studio" onClick={toggleMobileMenu} className={navLinkClassMobile}>Studio</NavLink>}
              {canSeeTab('generate') && <NavLink to="/generate" onClick={toggleMobileMenu} className={navLinkClassMobile}>Generate</NavLink>}
              {canSeeTab('series') && <NavLink to="/series" onClick={toggleMobileMenu} className={navLinkClassMobile}>Series</NavLink>}
              {canSeeTab('docs') && <NavLink to="/docs" onClick={toggleMobileMenu} className={navLinkClassMobile}>Docs</NavLink>}
              {canSeeTab('builder') && <NavLink to="/builder" onClick={toggleMobileMenu} className={navLinkClassMobile}>Builder</NavLink>}
              {canSeeTab('chat') && <NavLink to="/chat" onClick={toggleMobileMenu} className={navLinkClassMobile}>Chat</NavLink>}
              {canSeeTab('generators') && <NavLink to="/generators" onClick={toggleMobileMenu} className={navLinkClassMobile}>Generators</NavLink>}
              {canSeeTab('datasets') && <NavLink to="/datasets" onClick={toggleMobileMenu} className={navLinkClassMobile}>Datasets</NavLink>}
              {canSeeTab('training') && <NavLink to="/training" onClick={toggleMobileMenu} className={navLinkClassMobile}>Training</NavLink>}
              {canSeeTab('pipeline') && <NavLink to="/pipeline" onClick={toggleMobileMenu} className={navLinkClassMobile}>Pipeline</NavLink>}
              {canSeeTab('archive') && <NavLink to="/archive" onClick={toggleMobileMenu} className={navLinkClassMobile}>Archive</NavLink>}
              {hasFeature('upload_media') && <NavLink to="/media" onClick={toggleMobileMenu} className={navLinkClassMobile}>Media</NavLink>}
              {isAdmin && <NavLink to="/admin" onClick={toggleMobileMenu} className={navLinkClassMobile}>Admin</NavLink>}
              
              {hasFeature('upload_media') && <div className="mt-4 border-t border-[var(--border-subtle)]">
                <button
                  type="button"
                  onClick={() => { setShowUploadLibrary(true); toggleMobileMenu(); }}
                  className={actionLinkClassMobile}
                >
                  Upload Media
                </button>
              </div>}
            </div>
          </div>
        </div>
      )}

      <AppSettingsModal
        isOpen={showEnhancerSettings}
        onClose={() => setShowEnhancerSettings(false)}
        themeMode={themeMode}
        roundedMode={roundedMode}
        onThemeToggle={onThemeToggle}
        onRoundedToggle={onRoundedToggle}
      />

      <AttachmentLibraryModal
        isOpen={showUploadLibrary}
        mode="manage"
        generatedAssets={[]}
        initialSelectedMediaIds={[]}
        onClose={() => setShowUploadLibrary(false)}
      />
    </>
  );
};

export default Header;
